import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const ribbon = [
  "Court-Safe Guidance",
  "Generational Cycle Breaking",
  "Autonomy Building",
  "NNEB-Trained Practice",
  "Trauma-Informed Frameworks",
];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="container-edit pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-6"
               data-testid="hero-eyebrow">
              For Protective Mothers · Independent Consultancy
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98] text-brand-forest">
              Understand your<br />
              child's <em className="text-brand-moss not-italic font-light">behaviour</em><br />
              after contact.
            </h1>
            <p className="mt-10 text-lg leading-relaxed text-brand-charcoal max-w-xl">
              Ground-level, fiercely court-safe practical guidance from a
              trauma-aware Nursery Nurse — helping mothers decode complex
              behaviours and restore emotional safety at home.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center justify-center px-8 py-4 bg-brand-forest text-brand-bg text-sm tracking-wide hover:bg-brand-moss transition-colors rounded-sm"
              >
                Explore the Digital Library
              </Link>
              <Link
                to="/inquire"
                data-testid="hero-consultation-btn"
                className="inline-flex items-center justify-center px-8 py-4 border border-brand-forest text-brand-forest text-sm tracking-wide hover:bg-brand-forest hover:text-brand-bg transition-colors rounded-sm"
              >
                Request a 1:1 Consultation
              </Link>
            </div>
            <p className="mt-10 text-sm text-brand-muted max-w-md leading-relaxed">
              NNEB-trained. Decades of early-years practice. Lived experience of
              the family-court system. Nothing here will ever ask you to coach a
              child or escalate conflict.
            </p>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "200ms" }}>
            <div className="relative">
              <div className="absolute -inset-4 border border-brand-border rounded-tl-[4rem] rounded-br-[4rem]" />
              <img
                src="https://images.pexels.com/photos/18183665/pexels-photo-18183665.jpeg"
                alt="A mother holding her child in warm natural light"
                className="relative rounded-tl-[4rem] rounded-br-[4rem] w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* RIBBON */}
      <section className="border-y border-brand-border bg-brand-surface overflow-hidden">
        <div className="flex gap-12 py-6 whitespace-nowrap animate-[scroll_45s_linear_infinite]"
             style={{ animation: "scroll 45s linear infinite" }}>
          {[...ribbon, ...ribbon, ...ribbon].map((r, i) => (
            <span key={i} className="font-serif text-xl md:text-2xl text-brand-forest flex items-center gap-12">
              {r}
              <span className="text-brand-terracotta">✦</span>
            </span>
          ))}
        </div>
        <style>{`@keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-33.33%); } }`}</style>
      </section>

      {/* THE WORK */}
      <section className="container-edit section-pad">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-5">The Work</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-forest leading-[1.05]">
              An autonomy lab — quietly counter-balancing what your child meets elsewhere.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-brand-charcoal leading-relaxed">
            <p>
              Children returning from contact under coercive-control dynamics rarely
              arrive with words. They arrive with bodies — clenched, dissociated,
              panic-lying, picking fights with siblings. The instinct is to ask
              questions. The instinct is wrong.
            </p>
            <p>
              The work is to read what the body is saying, restore agency through
              ordinary household ritual, and document with a precision that survives
              cross-examination. Never to escalate. Never to coach. Never to give the
              other side a foothold.
            </p>
            <Link
              to="/about"
              data-testid="work-about-link"
              className="inline-block text-sm uppercase tracking-[0.25em] text-brand-terracotta border-b border-brand-terracotta pb-1"
            >
              About the methodology
            </Link>
          </div>
        </div>
      </section>

      {/* REPAIR GUIDES — COMING SOON */}
      <section className="bg-brand-surface border-y border-brand-border">
        <div className="container-edit section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-4">The Repair Guides</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-forest leading-tight">
                Quiet, downloadable guides —<br /><em className="text-brand-moss not-italic font-light">coming soon.</em>
              </h2>
              <p className="mt-6 text-brand-charcoal leading-relaxed max-w-xl">
                A small library of court-safe, trauma-informed repair guides for
                the hours after contact. Currently being finalised. Join the
                quiet waitlist to be the first to know when each one is ready.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to="/guides"
                data-testid="home-guides-cta"
                className="inline-flex items-center px-8 py-4 bg-brand-forest text-brand-bg text-sm tracking-wide hover:bg-brand-moss transition-colors rounded-sm"
              >
                Join the waitlist
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="container-edit section-pad">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <img
              src="https://images.pexels.com/photos/34287271/pexels-photo-34287271.jpeg"
              alt="A sunlit, quiet sitting room — the sanctuary aesthetic"
              className="w-full aspect-[4/3] object-cover rounded-sm"
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-5">1:1 Services</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-forest leading-tight">
              When the handbook<br />isn't enough.
            </h2>
            <p className="mt-8 text-brand-charcoal leading-relaxed">
              Private behavioural decoding sessions, custom household routine-mapping
              and independent consultancy — for mothers who need eyes on the specifics
              of their week.
            </p>
            <ul className="mt-8 space-y-4 text-brand-charcoal">
              {[
                "Strategy Session — prepare for the contact week ahead",
                "Behavioural Decoding — process the incident that just landed",
                "Custom Household Routine-Mapping — rebuild the steady contrast",
              ].map((s) => (
                <li key={s} className="flex items-start gap-4">
                  <span className="mt-2 inline-block w-6 h-px bg-brand-terracotta flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/services"
              data-testid="home-services-cta"
              className="mt-10 inline-flex items-center px-8 py-4 bg-brand-terracotta text-brand-bg text-sm tracking-wide hover:bg-brand-terracottaDark transition-colors rounded-sm"
            >
              See the 1:1 work
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-brand-forest text-brand-bg">
        <div className="container-edit section-pad">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-10">I see where you are</p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-brand-cream">
                You're holding a child<br />
                you cannot always reach,<br />
                <em className="not-italic font-light text-brand-terracotta">in language you're terrified to use.</em>
              </h2>
            </div>

            <div className="lg:col-span-5 space-y-6 text-brand-bg/85 leading-relaxed">
              <p>
                I know the moment. They've come home from contact and something is
                off. The body says it before the words do — a flatness behind the
                eyes, a meltdown over a sock, a small clenched lie. And you can
                feel yourself splitting in two.
              </p>
              <p>
                One half of you wants to gather them in and say <em className="text-brand-cream not-italic">"sweetheart, that wasn't right, you didn't deserve that, I love you, I love you, I love you"</em> — because they are six, or four, or nine, or any age frankly, and they need to hear it. Loving reassurance that they are not to blame.
              </p>
              <p>
                The other half is already mid-sentence and panicking. Was that
                leading? Could my words, if repeated, be twisted into alienation?
                Did I just hand the other side an opportunity to place blame, to
                reduce my contact time with my children? So you go quiet. And the
                quiet feels like a second betrayal — of them, and of yourself.
              </p>
              <p className="text-brand-cream font-serif text-2xl italic leading-snug">
                I have been there too.
              </p>
              <p>
                That bind is the whole reason this work exists. You should never
                have to choose between loving your child out loud and protecting
                them in court. There is language that does both. There are rituals
                that reassure without ever crossing into coaching. There is a way
                to be the soft place they land and the calm, documented witness
                their case needs — at the same time, in the same sentence.
              </p>
              <p className="text-brand-cream">
                That is what I teach. Quietly. Privately. Without ever asking you
                to be less of a mother than you are.
              </p>
            </div>
          </div>

          <p className="mt-16 text-sm uppercase tracking-[0.3em] text-brand-bg/60">
            — The Founder, NNEB
          </p>
        </div>
      </section>

      {/* IN THEIR WORDS */}
      <section className="container-edit section-pad" data-testid="in-their-words">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">In their words</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-forest leading-tight">
              Quiet notes,<br />sent on quiet weeks.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-brand-charcoal leading-relaxed self-end">
            <p>
              Every quote below is shared with written consent, after a cooling-off
              period, with every identifying detail softened or removed. No surnames.
              No locations. No specifics that could ever be traced back to a case.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
          {[
            {
              quote: "For the first time in two years, the Sunday after contact felt like a Sunday.",
              attr: "Mother of two · one year on",
            },
            {
              quote: "I stopped second-guessing every sentence I said to him. That alone gave me my voice back.",
              attr: "M. · six months on",
            },
          ].map((t, i) => (
            <figure
              key={i}
              className="border-l-2 border-brand-terracotta pl-8 py-2 animate-fade-up"
              style={{ animationDelay: `${i * 120}ms` }}
              data-testid={`testimonial-${i}`}
            >
              <blockquote className="font-serif text-2xl md:text-3xl leading-snug text-brand-forest italic">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 text-xs uppercase tracking-[0.3em] text-brand-muted">
                — {t.attr}
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="mt-16 text-xs text-brand-muted max-w-2xl leading-relaxed italic">
          Placeholder quotes. Real notes from mothers will be added here as consent is given.
          Each will be fully anonymised and reviewed for safeguarding risk before publication.
        </p>
      </section>

    </main>
  );
}
